import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from } from 'rxjs';
import { map, catchError, switchMap, tap, withLatestFrom, mergeMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import * as FormationActions from '../actions/formation.actions';
import { FormationService } from '../../service/formation.service';
import { OccurrenceService } from '../../service/occurrence.service';
import { UpcomingService } from '../../service/upcoming.service';
import { SessionService } from '../../service/session.service';
import { selectCurrentUser } from '../selectors/user.selectors';
import { environment } from '../../../../../environments/environment';
import { Occurrence } from '../../model/occurrence.model';
import { Session } from '../../model/session.model';

@Injectable()
export class FormationEffects {
  constructor(
    private actions$: Actions,
    private formationService: FormationService,
    private occurrenceService: OccurrenceService,
    private upcomingService: UpcomingService,
    private sessionService: SessionService,
    private store: Store
  ) {}

  // Load formations list
  loadFormations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormationActions.LoadFormations),
      withLatestFrom(this.store.select(selectCurrentUser)),
      switchMap(([action, user]) => {
        const studentId = action.studentId || user?.id;
        if (!studentId) {
          return of(FormationActions.LoadFormationsFailure({ error: 'Student ID not found' }));
        }

        
        return this.formationService.getFormationsByStudentId(studentId).pipe(
          map(formations => {
            // Sync avec BehaviorSubject pour compatibilité
            this.formationService.listeFormations.next(formations.results);
            return FormationActions.LoadFormationsSuccess({ formations: formations.results });
          }),
          catchError(error => {
            return of(FormationActions.LoadFormationsFailure({ 
              error: error.message || 'Failed to load formations' 
            }));
          })
        );
      })
    )
  );

  // Load formation detail
  loadFormationDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormationActions.LoadFormationDetail),
      switchMap(({ formationId }) =>
        this.formationService.getObservableFormationById(formationId).pipe(
          map(formation => {
            // Sync avec BehaviorSubject pour compatibilité
            this.formationService.currentFormationSubject.next(formation);
            return FormationActions.LoadFormationDetailSuccess({ formation });
          }),
          catchError(error => {
            console.error('Error loading formation detail', error);
            return of(FormationActions.LoadFormationDetailFailure({ 
              error: error.message || 'Failed to load formation detail' 
            }));
          })
        )
      )
    )
  );

  // Load formation detail when formation is selected (to get complete info)
  loadFormationDetailOnSelect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormationActions.SelectFormation),
      map(({ formation }) => {
        return FormationActions.LoadFormationDetail({ formationId: formation.id });
      })
    )
  );

  // Load next occurrence when formation detail is loaded
  loadNextOccurrenceOnDetailLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormationActions.LoadFormationDetailSuccess),
      map(({ formation }) => {
        return FormationActions.LoadNextOccurrence({ formationId: formation.id });
      })
    )
  );

  // Load next occurrence (logique legacy complète)
  loadNextOccurrence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormationActions.LoadNextOccurrence),
      switchMap(({ formationId }) => {
        // Appel API upcomings (comme legacy)
        const url = `${environment.server}upcomings/?salt=${Math.random()}&page_size=1&formation=${formationId}`;
        
        return this.upcomingService.getUpcomingsWithUrl(url).pipe(
          mergeMap((response: any) => {
            // Vérifier si résultats existent
            if (!response || !response.results || response.results.length === 0) {
              // Pas de prochaine occurrence
              this.formationService.nextOccurrence.next(null);
              return of(FormationActions.LoadNextOccurrenceSuccess({ occurrence: null }));
            }

            // Filtrer sur status === 'booked' (comme legacy)
            const bookedOccurrences = response.results.filter(
              (occ: Occurrence) => occ.status === 'booked'
            );

            if (bookedOccurrences.length === 0) {
              // Aucune occurrence bookée
              this.formationService.nextOccurrence.next(null);
              return of(FormationActions.LoadNextOccurrenceSuccess({ occurrence: null }));
            }

            const nextOccurrence: Occurrence = bookedOccurrences[0];

            // Vérifier si session existe
            if (nextOccurrence.session) {
              // Session déjà existante, on peut retourner l'occurrence
              this.formationService.nextOccurrence.next(nextOccurrence);
              return of(FormationActions.LoadNextOccurrenceSuccess({ occurrence: nextOccurrence }));
            } else {
              // Créer la session manquante (comme legacy)
              return this.formationService.getObservableFormationById(formationId).pipe(
                mergeMap((formation) => {
                  const body = {
                    formation: formation.url,
                    teacher: nextOccurrence.teacher.url,
                    occurrence: nextOccurrence.url
                  };

                  return this.sessionService.postNewSession(formation.url, body).pipe(
                    map((_newSession: Session) => {
                      // Session créée, on met à jour nextOccurrence avec session
                      const updatedOccurrence = { ...nextOccurrence, session: _newSession };
                      this.formationService.nextOccurrence.next(updatedOccurrence);
                      return FormationActions.LoadNextOccurrenceSuccess({ occurrence: updatedOccurrence });
                    }),
                    catchError((sessionError) => {
                      console.error('Error creating session for next occurrence', sessionError);
                      // Même en erreur, on retourne l'occurrence sans session
                      this.formationService.nextOccurrence.next(nextOccurrence);
                      return of(FormationActions.LoadNextOccurrenceSuccess({ occurrence: nextOccurrence }));
                    })
                  );
                }),
                catchError((formationError) => {
                  console.error('Error loading formation for session creation', formationError);
                  // Même en erreur formation, on retourne l'occurrence
                  this.formationService.nextOccurrence.next(nextOccurrence);
                  return of(FormationActions.LoadNextOccurrenceSuccess({ occurrence: nextOccurrence }));
                })
              );
            }
          }),
          catchError((error) => {
            console.error('Error loading next occurrence', error);
            this.formationService.nextOccurrence.next(null);
            return of(FormationActions.LoadNextOccurrenceFailure({ 
              error: error.message || 'Failed to load next occurrence' 
            }));
          })
        );
      })
    )
  );

  // Update formation
  updateFormation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FormationActions.UpdateFormation),
      switchMap(({ formationUrl, params }) =>
        this.formationService.patchObservableFormation(formationUrl, params).pipe(
          map(formation => {
            // Sync avec BehaviorSubject pour compatibilité
            if (this.formationService.currentFormationSubject.value?.url === formationUrl) {
              this.formationService.currentFormationSubject.next(formation);
            }
            return FormationActions.UpdateFormationSuccess({ formation });
          }),
          catchError(error => {
            console.error('Error updating formation', error);
            return of(FormationActions.UpdateFormationFailure({ 
              error: error.message || 'Failed to update formation' 
            }));
          })
        )
      )
    )
  );
}
